import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { FilmStrip, Cards } from '@phosphor-icons/react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { DraftSummary, RenderJobDto, ThoughtSummary } from '@app/shared';
import { HelpHint } from '../components/HelpHint';
import { Badge, Button, Card, Segmented, Skeleton, SkeletonGroup } from '../components/ui';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { errorText } from '../lib/errors';
import { formatDate } from '../lib/format';
import { DRAFT_TONE, firstLine, kindLabel, renderCardLine, THOUGHT_TONE } from '../lib/labels';
import { renderApi } from '../lib/render-client';
import { readyCover } from '../lib/render-view';
import { haptic } from '../lib/tma';

const PAGE = 20;

/** Next page cursor: the id of the last row of a full page. */
const nextBefore = <T extends { id: number }>(last: T[]) => (last.length === PAGE ? last[last.length - 1]!.id : undefined);

function useList<T extends { id: number }>(key: string, load: (before?: number) => Promise<T[]>, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [key, 'library'],
    queryFn: ({ pageParam }) => load(pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: nextBefore,
    enabled,
  });
}

type Tab = 'thoughts' | 'texts' | 'ready';

const ROW = 'block p-3 rounded-xl bg-surface shadow-card transition-opacity active:opacity-80';

/** A finished build in «Готовое»: a 9:16 cover (or a format placeholder), the format in the corner. */
function ReadyTile({ job }: { job: RenderJobDto }) {
  const t = useT();
  const cover = readyCover(job);
  const [broken, setBroken] = useState(false);
  const reel = job.format === 'reel';
  return (
    <Link to={`/renders/${job.id}`} onClick={() => haptic('light')} className="block active:opacity-80">
      <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-surface-2">
        {cover && !broken ? (
          <img
            src={cover}
            alt=""
            loading="lazy"
            onError={() => setBroken(true)}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-hint">
            {reel ? <FilmStrip size={32} /> : <Cards size={32} />}
          </div>
        )}
        <Badge tone="ink" className="absolute top-2 left-2" leftIcon={reel ? <FilmStrip size={12} /> : <Cards size={12} />}>
          {reel ? t.ideas.reel : t.ideas.carousel}
        </Badge>
      </div>
      <p className="mt-1.5 text-xs text-hint line-clamp-2">{renderCardLine(job, t)}</p>
    </Link>
  );
}

/** The library (spec 6.1 «Копилка»): thoughts, texts and the archive of finished builds. */
export function LibraryPage() {
  const t = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = raw === 'texts' || raw === 'ready' ? raw : 'thoughts';

  const thoughts = useList<ThoughtSummary>('thoughts', (b) => api.listThoughts(b), tab === 'thoughts');
  const drafts = useList<DraftSummary>('drafts', (b) => api.listDrafts(b), tab === 'texts');
  const ready = useList<RenderJobDto>('renders', async (b) => (await renderApi.listRenders(b)).items, tab === 'ready');
  const list = tab === 'thoughts' ? thoughts : tab === 'texts' ? drafts : ready;
  const rows = list.data?.pages.flat() ?? [];

  return (
    <div className="pt-2">
      <h1 className="flex items-center gap-2 text-xl font-extrabold mb-4">
        {t.library.title}
        <HelpHint topic="library" />
      </h1>

      <Segmented<Tab>
        className="mb-4"
        value={tab}
        options={[
          { value: 'thoughts', label: t.library.thoughts },
          { value: 'texts', label: t.library.texts },
          { value: 'ready', label: t.render.readyTab },
        ]}
        onChange={(value) => setParams(value === 'thoughts' ? {} : { tab: value }, { replace: true })}
      />

      {list.isLoading &&
        (tab === 'ready' ? (
          <SkeletonGroup label={t.common.loading} className="grid grid-cols-2 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="aspect-[9/16]" />
            ))}
          </SkeletonGroup>
        ) : (
          <SkeletonGroup label={t.common.loading} className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </SkeletonGroup>
        ))}
      {list.isError && <p className="text-sm text-hint">{errorText(list.error, t)}</p>}
      {list.data && rows.length === 0 && (
        <Card variant="surface" padding="none" className="p-5 text-center">
          <p className="text-sm text-hint mb-4">
            {tab === 'thoughts' ? t.library.emptyThoughts : tab === 'texts' ? t.library.emptyTexts : t.render.emptyReady}
          </p>
          <Button variant="ink" onClick={() => navigate('/')}>
            {t.library.emptyCta}
          </Button>
        </Card>
      )}

      {tab === 'ready' ? (
        <ul className="grid grid-cols-2 gap-x-2 gap-y-3">
          {(rows as RenderJobDto[]).map((r) => (
            <li key={r.id}>
              <ReadyTile job={r} />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="space-y-2">
          {tab === 'thoughts'
            ? (rows as ThoughtSummary[]).map((th) => (
                <li key={th.id}>
                  <Link to={`/thoughts/${th.id}`} onClick={() => haptic('light')} className={ROW}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs text-hint">{formatDate(th.createdAt)}</span>
                      <Badge tone={THOUGHT_TONE[th.status]}>{t.home.status[th.status]}</Badge>
                    </div>
                    <p className="text-sm truncate">{firstLine(th.excerpt)}</p>
                    {th.ideasCount > 0 && (
                      <p className="text-xs text-hint mt-1">
                        {t.library.counts(th.ideasCount, th.draftsCount, th.approvedCount)}
                      </p>
                    )}
                  </Link>
                </li>
              ))
            : (rows as DraftSummary[]).map((d) => (
                <li key={d.id}>
                  <Link to={`/drafts/${d.id}`} onClick={() => haptic('light')} className={ROW}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 text-xs text-hint">
                        {d.format === 'reel' ? <FilmStrip size={12} /> : <Cards size={12} />}
                        {kindLabel(d.format, d.type, t)}
                        {' · '}
                        {formatDate(d.updatedAt)}
                      </span>
                      <Badge tone={DRAFT_TONE[d.status]}>{t.library.draftStatus[d.status]}</Badge>
                    </div>
                    <p className="text-sm font-medium break-words line-clamp-2">{d.hook ?? ''}</p>
                  </Link>
                </li>
              ))}
        </ul>
      )}

      {list.hasNextPage && (
        <Button
          variant="secondary"
          fullWidth
          className="mt-3"
          loading={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          {t.library.more}
        </Button>
      )}
    </div>
  );
}
